/**
 * NOTE: This file is part of the legacy Notes feature.
 * User-facing text has been renamed to "Documentation" for clarity.
 * For the new Notes feature, check the Hub implementation.
 */

import { NoteReminderForm } from "@/components/conversation-details/notes/note-reminder-form";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu.tsx";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip.tsx";
import { Note } from "@/lib/api/notes.ts";
import { UserRole } from "@/lib/api/user";
import {
  NoteSchema,
  useNoteForm,
} from "@/lib/hooks/conversations/use-note-form.tsx";
import { useNoteUtils } from "@/lib/hooks/conversations/use-note-utils.ts";
import { cn } from "@/lib/utils.ts";
import {
  Calendar,
  Check,
  Loader2,
  Share2,
  Trash,
  TrashIcon,
  VideoIcon,
} from "lucide-react";
import { Fragment, useEffect, useMemo, useRef } from "react";
import TextareaAutosize from "react-textarea-autosize";
import { useAuth } from "@/components/auth/auth-context";

export interface NoteCardProps {
  readonlyMode?: boolean;
  adminMode?: boolean;
  isNew?: boolean;
  editMode?: boolean;

  note?: Note & {
    metadata?: {
      hasRecording?: boolean;
      canResubmit?: boolean;
      type?: string;
      latestUpdate?: boolean;
    };
  };

  noteActions: {
    togglePin?: () => void;
    onDelete?: () => void;
    handleSave: (payload: NoteSchema) => Promise<void>;
    shareWith?: (shareWith: "MENTEE" | "MENTOR") => void;
  };

  cardActions?: {
    onClick?: () => void;
    onCancel?: () => void;
  };

  isSubmitting?: boolean;
  noWrapper?: boolean;

  isSharedWithMentee?: boolean;
  isSharedWithMentor?: boolean;

  isLatestNote?: boolean;
}

export function NoteCard({
  isNew = false,
  editMode = false,
  noteActions: { togglePin, onDelete, handleSave, shareWith },
  cardActions: { onClick, onCancel } = {},
  note,
  readonlyMode,
  noWrapper,
  isSubmitting,
  adminMode,
  isSharedWithMentee,
  isSharedWithMentor,
}: NoteCardProps) {
  const { user } = useAuth();
  const { title, content, metadata, reminder, createdBy } = note || {
    title: "",
    content: "",
    metadata: {},
    createdBy: undefined,
  };

  const {
    isNotePinned,
    resetNoteForm,
    setNoteFormValues,
    convertReminderToFormValues,
  } = useNoteUtils();
  const isPinned = note ? isNotePinned(note) : false;

  const form = useNoteForm({
    title: isNew ? "" : title,
    content: isNew ? "" : content,
    reminderVisible: !!reminder,
    metadata: isNew ? {} : metadata,
    ...(reminder && !isNew
      ? convertReminderToFormValues(
          reminder,
          ((metadata as any)?.reminderTimezone as string) || "",
        )
      : {
          date: undefined,
          time: undefined,
          timezone: "",
        }),
  });

  const {
    title: noteTitle,
    content: noteContent,
    reminderVisible,
    date,
    time,
    timezone,
  } = form.watch();

  const noteContentRef = useRef<HTMLTextAreaElement>(null);

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    form.setValue("content", e.target.value);
  };

  const showDoneButton =
    (isNew && (!!noteTitle?.length || noteContent?.length > 0)) || editMode;

  const disableDoneButton = noteContent?.length === 0;

  const shouldHighlight = showDoneButton || editMode;

  const saveNote = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();

    if (!disableDoneButton) {
      await handleSave?.(form.getValues());

      if (isNew) {
        resetNoteForm(form);
      }
    }
  };

  useEffect(() => {
    let listener: (e: KeyboardEvent) => void = () => {};

    if (showDoneButton) {
      listener = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          if (isNew) {
            resetNoteForm(form);
          } else if (note) {
            setNoteFormValues(form, note);
          }

          onCancel?.();
        } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
          !disableDoneButton && saveNote();
        }
      };

      window.addEventListener("keydown", listener);
    }

    return () => {
      window.removeEventListener("keydown", listener);
    };
  }, [showDoneButton, note, isNew, saveNote, onCancel, disableDoneButton]);

  useEffect(() => {
    // Sync note with parent component
    if (!isNew && !editMode && note) {
      setNoteFormValues(form, note);
    }
  }, [note, isNew, editMode]);

  const shareWithMenuOptions = useMemo(() => {
    // const shareWithMentee = {
    //   label: isSharedWithMentee ? "Unshare with mentee" : "Share with mentee",
    //   action: () => {
    //     shareWith?.("MENTEE");
    //   },
    // };

    const shareWithMentor = {
      label: isSharedWithMentor ? "Unshare with mentor" : "Share with mentor",
      action: () => {
        shareWith?.("MENTOR");
      },
    };

    if (adminMode) {
      return [shareWithMentor];
    }

    if (user?.role === UserRole.MENTOR) {
      return [];
    }

    if (user?.id === createdBy?.id && !adminMode) {
      return [];
    }

    return [];
  }, [adminMode, user, createdBy, isSharedWithMentor, shareWith]);

  const Content = useMemo(() => {
    return (
      <Fragment>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <TextareaAutosize
              readOnly={!editMode && !isNew}
              className="w-full resize-none border-0 bg-transparent text-base font-bold leading-tight text-black outline-none"
              placeholder="add title"
              value={noteTitle}
              onChange={(e) => form.setValue("title", e.target.value)}
              cacheMeasurements
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  noteContentRef.current?.focus();
                }
              }}
            />
            {!isNew && (
              <div className="flex flex-shrink-0 items-center gap-2">
                {(isSharedWithMentee || isSharedWithMentor) && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="inline-flex cursor-default items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                          <Share2 className="mr-1 h-3 w-3" />
                          Shared
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          Shared with{" "}
                          {[
                            isSharedWithMentee && "mentee",
                            isSharedWithMentor && "mentor",
                          ]
                            .filter(Boolean)
                            .join(" and ")}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {createdBy && (
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
                    Created by{" "}
                    {createdBy.id === user?.id ? "You" : createdBy.name}
                  </span>
                )}
              </div>
            )}
          </div>

          <TextareaAutosize
            readOnly={!editMode && !isNew}
            value={noteContent}
            onChange={handleContentChange}
            className="w-full resize-none border-0 bg-transparent text-sm font-normal text-neutral-400 outline-none"
            placeholder={isNew ? "start typing to create documentation" : ""}
            ref={noteContentRef}
            cacheMeasurements
            minRows={1}
          />

          <div className="mt-2 flex gap-2">
            {metadata?.hasRecording && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(
                    `/mentor-monthly-updates/${note?.mentorshipId}?videoId=${note?.metadata?.recordingId}`,
                    "_blank",
                  );
                }}
                className="flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
              >
                <VideoIcon size={16} />
                View recording
              </button>
            )}
            {metadata?.canResubmit &&
              metadata?.type === "mentor_monthly_update" &&
              metadata?.latestUpdate && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(
                      `/mentor-monthly-updates/${note?.mentorshipId}`,
                      "_blank",
                    );
                  }}
                  className="flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
                >
                  <Share2 size={16} />
                  Resubmit
                </button>
              )}
          </div>

          {showDoneButton && (
            <div
              className={cn("mt-2 flex flex-row justify-between", {
                "items-center": !reminderVisible,
                "items-end": reminderVisible,
              })}
            >
              <div className="mr-4 w-full">
                <div className="flex flex-row items-center gap-4">
                  <button
                    className={cn("flex flex-row items-center gap-1", {
                      "cursor-pointer": !reminder,
                      "cursor-default": reminder,
                    })}
                    onClick={() => {
                      if (reminder && reminderVisible) return;

                      if (reminderVisible) {
                        // reset reminder form
                        form.setValue("date", undefined);
                        form.setValue("time", undefined);
                        form.setValue("timezone", "");
                        form.setValue("reminderVisible", false);
                      } else {
                        // check if reminder is already set
                        if (reminder) {
                          const { date, time, timezone } =
                            convertReminderToFormValues(
                              reminder,
                              ((metadata as any)?.reminderTimezone as string) ||
                                "",
                            ) || {};
                          form.setValue("date", date);
                          form.setValue("time", time);
                          form.setValue("timezone", timezone);
                        }

                        form.setValue("reminderVisible", true);
                      }
                    }}
                  >
                    <Calendar size={16} className="text-orange-400" />
                    <span className="text-xs font-normal leading-relaxed text-zinc-400">
                      {reminderVisible ? "Reminder" : "Add reminder"}
                    </span>
                  </button>

                  {reminder && (
                    <button
                      className="flex flex-row items-center gap-1"
                      onClick={(e) => {
                        e.stopPropagation();

                        form.setValue("reminderVisible", false);
                        form.setValue("date", undefined);
                        form.setValue("time", undefined);
                        form.setValue("timezone", "");

                        handleSave({
                          title: noteTitle,
                          content: noteContent,
                          metadata: {},
                          timezone: "",
                          time: undefined,
                          date: undefined,
                          reminderVisible: false,
                        });
                      }}
                    >
                      <TrashIcon size={16} className="text-red-500" />
                      <span className="text-xs font-normal leading-relaxed text-red-500">
                        Delete Reminder
                      </span>
                    </button>
                  )}
                </div>

                {reminderVisible && (
                  <NoteReminderForm
                    setValue={(key, value) => form.setValue(key as any, value)}
                    date={date}
                    time={time}
                    timezone={timezone}
                  />
                )}
              </div>
              <button
                className={cn(
                  "flex items-center justify-center transition-opacity active:opacity-50",
                  {
                    "mb-2": reminderVisible,
                  },
                )}
                onClick={saveNote}
                disabled={disableDoneButton || isSubmitting}
              >
                {isSubmitting ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  <Check className="text-gray-400" size={20} />
                )}
              </button>
            </div>
          )}
        </div>
      </Fragment>
    );
  }, [
    noteTitle,
    noteContent,
    reminderVisible,
    reminder,
    disableDoneButton,
    isSubmitting,
    handleSave,
    date,
    time,
    timezone,
  ]);

  if (noWrapper) {
    return <Fragment>{Content}</Fragment>;
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger className="w-full">
        <div
          className={cn(
            "relative h-auto w-full border-spacing-4 rounded-xl border-2 border-zinc-300 bg-white px-4 py-3",
            {
              "border-indigo-500": shouldHighlight,
              "border-dashed": isNew && !shouldHighlight,
            },
          )}
          onClick={onClick}
        >
          {Content}
        </div>
      </ContextMenuTrigger>

      {!isNew && !readonlyMode && (togglePin || onDelete || shareWith) && (
        <ContextMenuContent className="p-0">
          {togglePin && (
            <ContextMenuItem
              className="cursor-pointer px-4 py-2"
              onClick={togglePin}
            >
              {isPinned ? "Unpin Documentation" : "Pin Documentation"}
            </ContextMenuItem>
          )}
          {shareWith &&
            shareWithMenuOptions.map(({ label, action: handleClick }) => (
              <ContextMenuItem
                key={label}
                className="cursor-pointer px-4 py-2"
                onClick={handleClick}
              >
                {label}
              </ContextMenuItem>
            ))}
          {onDelete && (
            <ContextMenuItem
              className="flex cursor-pointer gap-2 border-t px-4 py-2"
              onClick={onDelete}
            >
              <Trash size={16} className="text-red-500" />
              <span className="text-sm font-normal leading-relaxed text-red-500">
                Delete Documentation
              </span>
            </ContextMenuItem>
          )}
        </ContextMenuContent>
      )}
    </ContextMenu>
  );
}
